import {
  ApiErrorText,
  Button,
  Card,
  Loader,
  ModalComponentWrapper,
} from '@/shared/components';
import {
  cancelOperation,
  useOperationDetails,
  useOperationDetailsPdf,
  useOperationStatusIcons,
} from '@/shared/api';
import type { OperationDetailsResponse, OperationDto } from '@/shared/types';
import { tv } from 'tailwind-variants';
import { useTranslation } from 'react-i18next';
import { useConfirmationModal } from '@/widgets';
import { CopyButton, OpenInNewTabButton } from '@/shared/components';
import { useAfterOperationRefresh, useCancelOperationResult } from '../model';
import { useCallback, useEffect, useMemo } from 'react';
import { useSessionStore } from '@/entities/session';
import PdfDownloadIcon from '@/shared/assets/pdf-download-icon.svg?react';

type ParamRowProps = {
  name: string;
  value: string;
  icon?: string;
  isCopyable?: boolean;
  isOpenable?: boolean;
};

type ModalProps = {
  operation: OperationDto;
  onClose: () => void;
  onStatusChange?: () => void;
};

const classes = tv({
  slots: {
    content: 'flex flex-col gap-[1.6rem]',
    card: 'px-[2.4rem] py-[1.6rem] bg-secondary-bg rounded-[1.6rem] shadow-none',
    row: 'grid grid-cols-[10rem_1fr] gap-[2.4rem] items-baseline',
    statusRow: 'grid grid-cols-[10rem_1fr] gap-[2.4rem] items-center',
    label: 'p4 text-secondary',
    param:
      'headline4 text-primary flex items-center justify-between gap-[1rem]',
    paramValue: 'flex items-center gap-[1rem] grow wrap-anywhere',
    paramActions: 'flex items-center gap-[1rem]',
    icon: 'w-[1.6rem] h-[1.6rem]',
  },
});

export const ParamRow = ({
  name,
  value,
  icon,
  isCopyable,
  isOpenable,
}: ParamRowProps) => {
  const c = classes();
  const isStatusRow = Boolean(icon);

  return (
    <div className={isStatusRow ? c.statusRow() : c.row()}>
      <div className={c.label()}>{name}</div>

      <div className={c.param()}>
        <div className={c.paramValue()}>
          {icon && <img src={icon} alt={name} className={c.icon()} />}

          {value}
        </div>
        {(isCopyable || isOpenable) && (
          <div className={c.paramActions()}>
            {isCopyable && (
              <CopyButton
                value={value}
                size="md"
                variant="secondary"
                buttonClassName="p-0"
              />
            )}
            {isOpenable && (
              <OpenInNewTabButton
                url={value}
                size="md"
                variant="secondary"
                buttonClassName="p-0"
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export function OperationHistoryDetailsModal({
  operation,
  onClose,
  onStatusChange,
}: ModalProps) {
  const { session } = useSessionStore();
  const { goToSuccessScreen, goToErrorScreen } = useCancelOperationResult();
  const afterOperationRefresh = useAfterOperationRefresh();
  const { t } = useTranslation();
  const c = classes();
  const { openModal, Modal: ConfirmationModal } = useConfirmationModal();
  const { operationsStatusIconsData, isOperationStatusIconsLoading } =
    useOperationStatusIcons();

  const eWallet = session?.additional?.eWallet;
  const operationId = operation.operaionid || '';

  const {
    data: operationDetails,
    isLoading: isOperationDetailsLoading,
    error: operationDetailsError,
  } = useOperationDetails<OperationDetailsResponse>({
    operationId,
    refetchInterval: 0,
  });

  const { data: operationDetailsPdf, isLoading: isOperationDetailsPdfLoading } =
    useOperationDetailsPdf<Blob>({
      operationId,
      enabled: operationDetails?.isPrintable === 1,
      refetchInterval: 0,
    });

  const isLoading =
    isOperationDetailsLoading ||
    isOperationStatusIconsLoading ||
    !operationDetails ||
    !operationsStatusIconsData;

  const isCancelable = operationDetails?.isCancelable === 1;
  const isPrintable = operationDetails?.isPrintable === 1;

  const pdfUrl = useMemo(() => {
    if (!operationDetailsPdf) return null;

    const file = new File(
      [operationDetailsPdf],
      `OperationsDetails_${eWallet}.pdf`,
      { type: 'application/pdf' }
    );

    return URL.createObjectURL(file);
  }, [operationDetailsPdf, eWallet]);

  useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  const handleOpenOperationPdfClick = useCallback(() => {
    if (!pdfUrl || isOperationDetailsPdfLoading || !isPrintable) return;

    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = `OperationsDetails_${eWallet}.pdf`;
    link.click();
  }, [isOperationDetailsPdfLoading, pdfUrl, isPrintable, eWallet]);

  useEffect(() => {
    if (!operationDetails || !operation) return;

    const oldStatus = operation.status;
    const newStatus = operationDetails.status.paramStatus;

    if (oldStatus !== newStatus) {
      onStatusChange?.();
    }
  }, [operation, operationDetails]);

  if (isLoading) {
    return <Loader />;
  }

  if (operationDetailsError) {
    return <ApiErrorText error={operationDetailsError} />;
  }

  const { status, blocks = [] } = operationDetails;

  const statusIcon = operationsStatusIconsData.value.find(
    icon => icon.status === status.paramStatus
  )?.icon;

  const handleCancelOperationClick = async (operation: OperationDto) => {
    try {
      await cancelOperation(operation.operaionid);
      afterOperationRefresh();
      goToSuccessScreen();
    } catch {
      goToErrorScreen();
    }
  };

  const handleButtonClick = async () => {
    if (isCancelable) {
      openModal({
        title: t('confirmationModal.cancelOperation.title'),
        description: t('confirmationModal.cancelOperation.description'),
        confirmText: t('confirmationModal.cancelOperation.confirmText'),
        cancelText: t('confirmationModal.cancelOperation.cancelText'),
        onConfirm: () => handleCancelOperationClick(operation),
      });
    } else {
      onClose();
    }
  };

  return (
    <ModalComponentWrapper
      title={operation.operationSubTypeName}
      footer={
        <div className="w-full flex flex-col gap-[1.6rem]">
          {isPrintable && (
            <Button
              size="lg"
              className="w-full"
              loading={isOperationDetailsPdfLoading}
              disabled={isOperationDetailsPdfLoading || !operationDetailsPdf}
              onClick={handleOpenOperationPdfClick}>
              <PdfDownloadIcon className={'w-[2.4rem] h-[2.4rem]'} />{' '}
              {t(`historyPage.printOperation.download`)}
            </Button>
          )}
          <Button
            size="lg"
            variant="secondary"
            className="w-full"
            onClick={handleButtonClick}>
            {isCancelable
              ? t('historyPage.cancelOperation.submitButton')
              : t('common.close')}
          </Button>
        </div>
      }>
      <div className={c.content()}>
        <Card className={c.card()}>
          <ParamRow
            name={status.paramName}
            value={status.paramValue}
            icon={statusIcon}
          />
        </Card>

        {blocks.map((block, index) => (
          <Card className={c.card()} key={index}>
            {block.block.map(param => (
              <ParamRow
                key={param.paramId}
                name={param.paramName}
                value={param.paramValue}
                isCopyable={Boolean(param.paramCopy)}
                isOpenable={Boolean(param.paramOpen)}
              />
            ))}
          </Card>
        ))}
      </div>

      {ConfirmationModal}
    </ModalComponentWrapper>
  );
}
